// src/contexts/servicos/TipoEquipamentoContext.js
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import TipoEquipamentoCad from '../../pages/servicos/cadastros/TipoEquipamentoCad';
import TipoEquipamentoSearch from '../../pages/servicos/cadastros/TipoEquipamentoSearch';
import { deleteData, fetchData, postData, putData } from '../../services/Api';
import { useAlert } from '../geral/AlertContext';
import { useMessage } from '../geral/MessageContext';

const TipoEquipamentoContext = createContext();

export const useTipoEquipamento = () => {
  const context = useContext(TipoEquipamentoContext);
  if (!context) {
    throw new Error('useTipoEquipamento deve ser usado dentro de TipoEquipamentoProvider.');
  }
  return context;
};

export const TipoEquipamentoProvider = ({ children }) => {
  const [tiposEquipamento, setTiposEquipamento] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showCad, setShowCad] = useState(false);
  const [tipoInicial, setTipoInicial] = useState(null);
  const onSelectRef = useRef(null);
  const onSaveRef = useRef(null);
  const { showAlert } = useAlert();
  const { showMessage } = useMessage();

  const executar = useCallback(async (acao, mensagemSucesso, mensagemErro) => {
    setLoading(true);
    try {
      const result = await acao();
      if (mensagemSucesso) showAlert(mensagemSucesso);
      return { sucesso: true, dados: result };
    } catch (error) {
      console.error(mensagemErro, error);
      showMessage(`${mensagemErro}\n${error?.message || error}`, 'danger');
      return { sucesso: false, dados: null };
    } finally {
      setLoading(false);
    }
  }, [showAlert, showMessage]);

  const listarTiposEquipamento = useCallback(async ativo => {
    const resultado = await executar(
      () => fetchData('/TipoEquipamento', ativo === undefined ? {} : { ativo }),
      null,
      'Erro ao buscar tipos de equipamento!',
    );
    if (!resultado.sucesso) return null;
    setTiposEquipamento(resultado.dados || []);
    return resultado.dados || [];
  }, [executar]);

  const adicionarTipoEquipamento = async tipo => {
    const resultado = await executar(
      () => postData('/TipoEquipamento', tipo),
      'Tipo de equipamento adicionado com sucesso.',
      'Erro ao adicionar tipo de equipamento!',
    );
    if (!resultado.sucesso) return null;
    setTiposEquipamento(anteriores => [...anteriores, resultado.dados]);
    return resultado.dados;
  };

  const atualizarTipoEquipamento = async tipo => {
    const resultado = await executar(
      () => putData(`/TipoEquipamento/${tipo.id}`, tipo),
      'Tipo de equipamento alterado com sucesso.',
      'Erro ao alterar tipo de equipamento!',
    );
    if (!resultado.sucesso) return null;
    setTiposEquipamento(anteriores => anteriores.map(
      item => item.id === resultado.dados.id ? resultado.dados : item,
    ));
    return resultado.dados;
  };

  const excluirTipoEquipamento = async id => {
    const resultado = await executar(
      () => deleteData('/TipoEquipamento', { tipoEquipamentoId: id }),
      'Tipo de equipamento excluído com sucesso.',
      'Erro ao excluir tipo de equipamento!',
    );
    if (!resultado.sucesso) return false;
    setTiposEquipamento(anteriores => anteriores.filter(item => item.id !== id));
    return true;
  };

  const handleShowTipoEquipamentoSearch = () => new Promise(resolve => {
    onSelectRef.current = resolve;
    setShowSearch(true);
  });

  const fecharSearch = () => {
    setShowSearch(false);
    onSelectRef.current?.(null);
    onSelectRef.current = null;
  };

  const selecionarTipoEquipamento = tipo => {
    const resolver = onSelectRef.current;
    onSelectRef.current = null;
    setShowSearch(false);
    resolver?.(tipo || null);
  };

  const showTipoEquipamentoCad = (tipo = null) => new Promise(resolve => {
    setTipoInicial(tipo);
    onSaveRef.current = resolve;
    setShowCad(true);
  });

  const fecharCad = () => {
    setShowCad(false);
    setTipoInicial(null);
    onSaveRef.current?.(null);
    onSaveRef.current = null;
  };

  const tipoSalvo = tipo => {
    const resolver = onSaveRef.current;
    onSaveRef.current = null;
    resolver?.(tipo || null);
  };

  const value = useMemo(() => ({
    tiposEquipamento,
    loading,
    listarTiposEquipamento,
    adicionarTipoEquipamento,
    atualizarTipoEquipamento,
    excluirTipoEquipamento,
    handleShowTipoEquipamentoSearch,
    showTipoEquipamentoCad,
  }), [tiposEquipamento, loading, listarTiposEquipamento]);

  return (
    <TipoEquipamentoContext.Provider value={value}>
      {children}
      <TipoEquipamentoSearch
        show={showSearch}
        onHide={fecharSearch}
        onSelectTipoEquipamento={selecionarTipoEquipamento}
      />
      <TipoEquipamentoCad
        show={showCad}
        onHide={fecharCad}
        initialTipoEquipamento={tipoInicial}
        onSaveTipoEquipamento={tipoSalvo}
      />
    </TipoEquipamentoContext.Provider>
  );
};
